import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, X } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { usePopup } from '../context/PopupContext';

const PricingSection = ({ service = 'trademark-registration' }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const { openPopup } = usePopup();

  const pricingData = {
    'trademark-registration': [
      {
        name: 'Basic',
        price: 45,
        description: '',
        features: [
          'Case Review',
          'Case Filing',
          'Basic Search',
          'State E-Filing',
          'Trademark Secured'
        ]
      },
      {
        name: 'Deluxe',
        price: 145,
        description: '',
        popular: true,
        features: [
          'Case Review',
          'Case Filing',
          'Basic Search',
          'State E-Filing',
          'Trademark Secured',
          'Direct Hit Search',
          'Dedicated Case Manager'
        ]
      },
      {
        name: 'Premium',
        price: 299,
        description: '',
        features: [
          'Case Review (by a TM Expert)',
          'Basic Free Trademark Search',
          'Trademark Monitoring Services (3 Months)',
          'Trademark Secured',
          'Refusal Risk Meter',
          'Case filing service with complete paperwork'
        ]
      },
      {
        name: 'Elite',
        price: 599,
        description: '',
        features: [
          'Case Review & Filing (U.S Registered IP Attorney)',
          'Assigned Case Filing Officer',
          'Private Registration',
          'Trademark Secured',
          'Federal Search & Clearance Report (All States)',
          'Federal E-Filing (USPTO)',
          'Cease & Desist Letter',
          'Same Day Filing',
          'Trademark Monitoring (1 Year)',
          'Trademark Secured',
          'Direct Hit Search',
          'Dedicated Case Manager',
          '24/7 Secure Online Access',
          '100% Approval Guarantee'
        ]
      }
    ],
    'copyright-registration': [
      {
        name: 'GOLD',
        price: 99,
        description: '+ USPTO Filing Fee',
        features: [
          'Professional Preparation of your federal copyright application including a review by the copyright team for accuracy, completeness and common mistakes',
          'Federal E-Filing - Electronic filing of your application with the USPTO with no need to wait for mail or dealing with paper files',
          'Certificate of Registration that will be mailed to you directly from the U.S. Copyright Office'
        ]
      },
      {
        name: 'Diamond',
        price: 149,
        description: '+ USPTO Filing Fee',
        popular: true,
        features: [
          'Cease & Desist Letter - A customer-specific form you can further customize if someone is infringing on your copyright. Avoid wasting time and money',
          'Transfer/Assignment - Custom assignment template. If you need to sell or otherwise convey your copyright, you have access to your pre-filled in template that you can further customize',
          '24 Hours Expedited Process of the preparation of your copyright application. Normal processing time is 5 business days in our Basic package'
        ]
      }
    ],
    'company-formation': [
      {
        name: 'START YOUR BUSINESS WITH EASE',
        price: null,
        description: '',
        features: [
          'Make sure you\'re not liable for any business debts. Ensure you\'re not on the hook for business liabilities',
          'Company Formation through a Simple Process: Our 3 step process discards with entering your details, Then selecting a service and lastly payment',
          'Save Money on Taxes: For potential savings you get to decide whether you\'re are taxed as a LLC or a Corporation'
        ],
        isInfo: true
      }
    ],
    'trademark-search': [
      {
        name: 'FEDERAL & STATE SEARCH',
        price: 149,
        description: '',
        features: [
          'Trademark search - The federal search provides any similar names, logos, or slogans that are registered or pending with the USPTO',
          'State Search - The state search does the same for all 50 states',
          'Detailed online report - Identifies any matches with all the information you need to strategize your next step'
        ]
      },
      {
        name: 'FEDERAL, STATE & COMMON LAW SEARCH',
        price: 299,
        description: '',
        popular: true,
        features: [
          'Includes the information from the Federal, State & Common Law Search, and also includes:',
          'Corporate name search - We run a search for all corporate and other business entity names in all 50 states',
          'Corporate Directories - We search the corporate directories for listing of others who might be using your name',
          'Common law - Proprietary internet searches reveal the use of your name that may be protected by common law trademarks',
          'Domain names - We search the most common top level domain names to see if anyone is using your name online'
        ]
      },
      {
        name: 'GLOBAL SEARCH',
        price: 499,
        description: '',
        features: [
          'Global search - We search for pending and registered marks in these multi-national jurisdictions:',
          'The European community',
          'World Intellectual Property Organization (WIPO)'
        ]
      }
    ],
    'amazon-brand-registry': [
      {
        name: 'Standard',
        price: 99,
        description: '+ USPTO filing fee',
        features: [
          'Amazon Brand Case Review',
          'Amazon Brand Case Filing',
          '100% Satisfaction Guarantee'
        ]
      },
      {
        name: 'Diamond',
        price: 149,
        description: '+ USPTO filing fee',
        popular: true,
        features: [
          'Amazon Brand Case Review',
          'Amazon Brand Case Preparation',
          'Amazon Brand Case Filing',
          '100% Satisfaction Guarantee',
          'Refusal Risk Meter'
        ]
      },
      {
        name: 'Premium',
        price: 199,
        description: '+ USPTO filing fee',
        features: [
          'Amazon Brand Case Review',
          'Amazon Brand Case Preparation',
          'Amazon Brand Case Filing',
          '100% Satisfaction Guarantee',
          'Refusal Risk Meter',
          'Amazon Brand Case Monitoring',
          'Complete Documentation',
          'Digital File',
          'Dedicated Case Manager'
        ]
      }
    ]
  };

  const packages = pricingData[service] || pricingData['trademark-registration'];

  const handleGetStarted = (pkg) => {
    setSelectedPackage(pkg);
    openPopup();
  };

  return (
    <div className="space-y-8">
      <div className={`grid grid-cols-1 ${packages.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : packages.length === 2 ? 'md:grid-cols-2' : packages.length === 1 ? 'md:grid-cols-1 max-w-2xl mx-auto' : 'md:grid-cols-3'} gap-8`}>
        {packages.map((pkg, index) => (
          <Card 
            key={index}
            className={`relative border-2 transition-all duration-300 hover:shadow-xl flex flex-col ${
              pkg.popular 
                ? 'border-[#C0392B] shadow-lg' 
                : 'border-[#7F8C8D]/20 hover:border-[#C0392B]/50'
            } ${pkg.isInfo ? 'bg-gradient-to-br from-[#C0392B]/5 to-[#C0392B]/10' : ''}`}
          >
            {pkg.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <span className="bg-[#C0392B] text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </span>
              </div>
            )}

            <CardHeader className="text-center pb-4">
              <CardTitle className="text-xl font-bold text-[#2C3E50] mb-2 min-h-[60px] flex items-center justify-center">
                {pkg.name}
              </CardTitle>
              {pkg.description && (
                <CardDescription className="text-[#7F8C8D] text-sm">
                  {pkg.description}
                </CardDescription>
              )}
              {pkg.price !== null && !pkg.isInfo && (
                <div className="pt-4">
                  <div className="text-5xl font-bold text-[#C0392B]">
                    ${pkg.price}
                  </div>
                  {pkg.description && pkg.description.includes('USPTO') && (
                    <p className="text-sm text-[#7F8C8D] mt-2">{pkg.description}</p>
                  )}
                  {!pkg.description && (
                    <p className="text-sm text-[#7F8C8D] mt-2">*(Govt. filing fee not included)</p>
                  )}
                </div>
              )}
            </CardHeader>

            <CardContent className="flex-grow">
              <div className="max-h-80 overflow-y-auto pr-2 pricing-scrollbar">
                <div className="space-y-3">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-[#C0392B] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#2C3E50] leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>

            {!pkg.isInfo && (
              <CardFooter className="mt-auto pt-6">
                <Button
                  onClick={() => handleGetStarted(pkg)}
                  className={`w-full py-6 text-lg font-semibold transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-[#C0392B] hover:bg-[#A93226] text-white'
                      : 'bg-white hover:bg-[#C0392B] text-[#C0392B] hover:text-white border-2 border-[#C0392B]'
                  }`}
                >
                  Get Started
                </Button>
              </CardFooter>
            )}
          </Card>
        ))}
      </div>

      {!packages[0]?.isInfo && (
        <div className="text-center text-sm text-[#7F8C8D] space-y-2 text-white">
          <p>* Government filing fees are separate and paid directly to the USPTO</p>
          <p>** All packages include money-back satisfaction guarantee</p>
        </div>
      )}
    </div>
  );
};

export default PricingSection;